import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, MapPin, Clock, Fuel, Store, Award, Navigation, Play, Bookmark, Share2,
  Info, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, TrendingDown,
} from "lucide-react";
import client, { money, apiErr, STORE_COLORS } from "@/lib/api";
import MapView from "@/components/MapView";
import FlowSteps from "@/components/FlowSteps";
import OpenInMaps from "@/components/OpenInMaps";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const PLAN_ICON = { best_overall: Award, cheapest: TrendingDown, one_stop: Store };

export default function Results() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [selected, setSelected] = useState("best_overall");
  const [whyOpen, setWhyOpen] = useState(true);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    client.get(`/lists/${listId}/plans`).then((r) => setDoc(r.data))
      .catch(async () => {
        try { const { data } = await client.post(`/lists/${listId}/plans`); setDoc(data); }
        catch (e) { toast.error(apiErr(e.response?.data?.detail)); navigate(`/list/${listId}`); }
      });
  }, [listId, navigate]);

  if (!doc) return (
    <div className="space-y-4"><Skeleton className="h-40 rounded-3xl" /><Skeleton className="h-64 rounded-3xl" /></div>
  );

  const plan = doc.plans.find((p) => p.plan_type === selected) || doc.plans[0];
  const home = [doc.prefs_snapshot?.home_lat || 51.0447, doc.prefs_snapshot?.home_lng || -114.0719];

  const startTrip = async () => {
    setStarting(true);
    try { const { data } = await client.post("/trips", { list_id: listId, plan_type: selected }); navigate(`/trip/${data.id}`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setStarting(false); }
  };
  const share = async () => {
    try { const { data } = await client.post(`/lists/${listId}/share`, { permission: "edit" });
      const url = window.location.origin + data.url;
      if (navigator.share) await navigator.share({ title: "Our grocery list", url }).catch(() => {});
      else { await navigator.clipboard?.writeText(url); toast.success("Share link copied — anyone with it can check items off"); } }
    catch { toast.error("Could not create share link"); }
  };

  return (
    <div className="space-y-6">
      <FlowSteps current={1} listId={listId} />
      <div className="relative overflow-hidden bg-pp-forest text-white rounded-3xl p-6 pp-shadow-lg pp-fade-up" data-testid="results-hero">
        <img src="/img/market.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#075B3A]/95 via-[#075B3A]/80 to-[#075B3A]/30" />
        <div className="relative">
        <p className="text-white/70 text-sm font-semibold uppercase tracking-wide">Your best plan</p>
        <div className="mt-2 flex flex-wrap items-end gap-x-8 gap-y-3">
          <div><p className="text-white/70 text-sm">{plan.title}</p><p className="text-4xl font-extrabold">{money(plan.checkout_total)}</p></div>
          <div><p className="text-white/70 text-sm">{plan.savings_vs_usual > 0.5 ? "You could save" : "Vs. your usual store"}</p><p className="text-3xl font-extrabold text-pp-gold">{plan.savings_vs_usual > 0.5 ? money(plan.savings_vs_usual) : "Same price"}</p></div>
          <div className="flex gap-6 text-sm">
            <div><p className="text-white/60">Stops</p><p className="font-bold text-lg">{plan.num_stores}</p></div>
            <div><p className="text-white/60">Trip time</p><p className="font-bold text-lg">{Math.round(plan.trip_minutes)} min</p></div>
            <div><p className="text-white/60">Distance</p><p className="font-bold text-lg">{plan.distance_km} km</p></div>
          </div>
        </div>
        </div>
      </div>

      {/* Plan cards */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-extrabold text-pp-text">Compare plans</h2>
          <button onClick={() => navigate("/profile")} className="text-sm font-semibold text-pp-forest hover:underline">Change preferences</button>
        </div>
        <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory py-2 px-1 -mx-1 scrollbar-none" data-testid="plan-cards">
          {doc.plans.map((p) => {
            const Icon = PLAN_ICON[p.plan_type] || Store;
            const active = p.plan_type === selected;
            return (
              <button key={p.plan_type} data-testid={`plan-card-${p.plan_type}`} onClick={() => setSelected(p.plan_type)}
                className={`snap-start shrink-0 w-64 text-left rounded-3xl p-5 border-2 transition-[transform,box-shadow,border-color] duration-200 ${active ? "border-pp-forest bg-white pp-shadow-lg" : "border-transparent bg-white pp-shadow hover:-translate-y-0.5"}`}>
                <div className="flex items-center justify-between">
                  <span className={`grid place-items-center h-9 w-9 rounded-xl ${active ? "bg-pp-forest text-white" : "bg-[#EBF7EE] text-pp-forest"}`}><Icon className="h-5 w-5" /></span>
                  {active && <CheckCircle2 className="h-5 w-5 text-pp-forest" />}
                </div>
                <p className="mt-3 font-extrabold text-pp-text">{p.title}</p>
                <p className="text-xs text-pp-muted h-8">{p.subtitle}</p>
                <p className="mt-2 text-2xl font-extrabold text-pp-forest">{money(p.checkout_total)}</p>
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-pp-muted">
                  <span className="rounded-full bg-pp-cream px-2 py-0.5">{p.num_stores} store{p.num_stores > 1 ? "s" : ""}</span>
                  <span className="rounded-full bg-pp-cream px-2 py-0.5">{Math.round(p.trip_minutes)} min</span>
                  <span className="rounded-full bg-[#EBF7EE] text-pp-forest px-2 py-0.5 font-semibold">save {money(Math.max(0, p.savings_vs_usual))}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* True trip cost breakdown */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <MapView stores={plan.stores} home={home} homeLabel={doc.prefs_snapshot?.home_label || doc.prefs_snapshot?.home_address || doc.prefs_snapshot?.postal_code} />

          {/* Why this plan */}
          <Collapsible open={whyOpen} onOpenChange={setWhyOpen}>
            <div className="bg-white rounded-3xl pp-shadow overflow-hidden">
              <CollapsibleTrigger asChild>
                <button data-testid="why-plan-toggle" className="w-full flex items-center justify-between p-5">
                  <span className="flex items-center gap-2 font-extrabold text-pp-text"><Info className="h-5 w-5 text-pp-info" /> Why this plan?</span>
                  {whyOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-5 pb-5 space-y-3">
                  <p className="text-pp-text">{plan.explanation}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                    {[
                      ["Basket subtotal", money(plan.subtotal)],
                      ["Estimated GST (5%)", money(plan.tax)],
                      ["Fuel cost", money(plan.fuel_cost)],
                      ["Time cost", money(plan.time_cost)],
                      ["Convenience penalty", money(plan.convenience_penalty)],
                      ["Loyalty value", "-" + money(plan.loyalty_value)],
                    ].map(([k, v]) => (
                      <div key={k} className="rounded-2xl bg-pp-cream p-3"><p className="text-pp-muted text-xs">{k}</p><p className="font-bold text-pp-text">{v}</p></div>
                    ))}
                  </div>
                  <div className="rounded-2xl bg-[#EBF7EE] p-3 flex items-center justify-between">
                    <span className="font-bold text-pp-forest">True effective trip cost</span>
                    <span className="text-xl font-extrabold text-pp-forest">{money(plan.true_cost)}</span>
                  </div>
                  <p className="text-xs text-pp-muted flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Prices from store data, flyers and shopper receipts — they can change in-store. Match confidence {Math.round(plan.match_confidence * 100)}%.</p>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          {/* Store-by-store */}
          {plan.stores.map((s, i) => (
            <div key={s.store_id} className="bg-white rounded-3xl p-5 pp-shadow" data-testid={`store-block-${s.code}`}>
              <div className="flex items-center gap-3">
                <span className="grid place-items-center h-9 w-9 rounded-full text-white font-bold" style={{ background: STORE_COLORS[s.code] || "#075B3A" }}>{i + 1}</span>
                <div className="flex-1"><p className="font-extrabold text-pp-text">{s.name}</p><p className="text-xs text-pp-muted">{s.address} · {s.hours}</p></div>
                <p className="font-extrabold text-pp-forest">{money(s.subtotal)}</p>
              </div>
              {s.membership_required && <p className="mt-2 text-xs text-[#8A5F00] bg-pp-gold/20 rounded-full px-2 py-0.5 inline-block">Membership required</p>}
              <div className="mt-3 divide-y divide-pp-border">
                {s.items.map((it, idx) => (
                  <div key={idx} className="py-2.5 flex items-center gap-3">
                    <img src={it.image} alt="" className="h-11 w-11 rounded-lg object-contain bg-white bg-pp-cream" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-pp-text text-sm truncate">{it.product_name}</p>
                      <p className="text-xs text-pp-muted">{it.brand} · {it.package} {it.unit_price ? `· ${money(it.unit_price)}${it.unit_price_label}` : ""}</p>
                      {it.note && <p className="text-[11px] text-pp-info">{it.note}</p>}
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-pp-text">{money(it.line_total)}</p>
                      {it.on_sale && <p className="text-[11px] text-pp-error font-semibold">SALE</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {plan.unavailable_items?.length > 0 && (
            <div className="bg-[#FDF0ED] rounded-3xl p-5" data-testid="unavailable-items">
              <p className="font-bold text-pp-error flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Not available nearby</p>
              <p className="text-sm text-pp-text mt-1">{plan.unavailable_items.join(", ")} — try adjusting brand/size or widen your radius.</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <div className="lg:sticky lg:top-24 bg-white rounded-3xl p-5 pp-shadow space-y-3">
            <Button data-testid="start-trip" onClick={startTrip} disabled={starting} className="w-full h-12 rounded-full bg-pp-forest text-base font-bold">
              {starting ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Play className="h-5 w-5 mr-2" /> Start Trip</>}
            </Button>
            <OpenInMaps home={home} stores={plan.stores} />
            <Button variant="outline" onClick={() => toast.success("Plan saved")} data-testid="save-plan" className="w-full h-11 rounded-full bg-white font-semibold"><Bookmark className="h-4 w-4 mr-2" /> Save Plan</Button>
            <Button variant="outline" onClick={share} data-testid="share-plan" className="w-full h-11 rounded-full bg-white font-semibold"><Share2 className="h-4 w-4 mr-2" /> Share List</Button>
            <div className="pt-2 border-t border-pp-border text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-pp-muted flex items-center gap-1"><Navigation className="h-3.5 w-3.5" /> Driving</span><span className="font-semibold">{plan.distance_km} km · {Math.round(plan.drive_minutes)} min</span></div>
              <div className="flex justify-between"><span className="text-pp-muted flex items-center gap-1"><Fuel className="h-3.5 w-3.5" /> Fuel</span><span className="font-semibold">{money(plan.fuel_cost)}</span></div>
              <div className="flex justify-between"><span className="text-pp-muted flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Shopping</span><span className="font-semibold">{Math.round(plan.shop_minutes)} min</span></div>
              <div className="flex justify-between"><span className="text-pp-muted flex items-center gap-1"><Award className="h-3.5 w-3.5" /> Loyalty value</span><span className="font-semibold text-pp-forest">{money(plan.loyalty_value)}</span></div>
              <div className="flex justify-between"><span className="text-pp-muted">Substitutions</span><span className="font-semibold">{plan.substitutions}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
